import { HttpClient } from '@angular/common/http';
import { inject, Inject, Injectable } from '@angular/core';
import { UntypedFormBuilder, FormGroup, Validators } from '@angular/forms';

import { Observable, map, catchError, of } from 'rxjs';

import { ResearchType } from '@empathy/global/data';

import { NotificationLevel, NotificationsService } from '@eiq/notifications';

import { EnvironmentConfig } from '../config/environment.config';
import { AudienceDefaultInvitationSettings } from '../models/audience/audience-default-invitation-settings.interface';
import { ResearchAudienceTypeEnum } from '../models/audience/audience-type.enum';
import { AudienceStats } from '../models/audience/audience-stats.interface';
import { IDeployToPanelAudience } from '../models/audience/deploy-to-panel/deploy-to-panel-audience.interface';

import { Query } from '../models/audience/deploy-to-panel/query/query.model';
import { IDirectRecruitmentAudience } from '../models/audience/direct-recruitment/direct-recruitment-audience.interface';
import { IDirectUploadAudience } from '../models/audience/direct-upload/direct-upload-audience.interface';
import { ResearchAudience } from '../models/audience/research-audience.model';
import { IParticipantEmailValidationResponse } from '../models/audience/target/participant-email-validation-response.interface';
import { TargetAudienceRow } from '../models/audience/target/target-audience-row.model';
import { ExternalProjectItem } from '../models/audience/third-party-provider/external-project-item.model';
import { IFeasibilityAndPrice } from '../models/audience/third-party-provider/feasibility-and-price.model';
import { IGetFeasibilityAndPriceRequest } from '../models/audience/third-party-provider/get-feasibility-and-price-request.model';
import { PureSpectrumIntegrationAttribute } from '../models/audience/third-party-provider/pure-spectrum-integration/pure-spectrum-integration-attribute.model';
import { AudienceStatusEnum } from '../models/audience/third-party-provider/audience-status.enum';
import { IThirdPartyProviderAudience } from '../models/audience/third-party-provider/third-party-provider-audience.interface';
import { IUpdateThirdPartyProviderAudience } from '../models/audience/third-party-provider/update-third-party-provider-audience.interface';
import { ProjectEndPages } from '../models/project-end-pages.interface';

@Injectable({
  providedIn: 'root'
})
export class AudienceService {
  private fb = inject(UntypedFormBuilder);
  private http = inject(HttpClient);
  private notificationService = inject(NotificationsService);

  constructor(@Inject('environment') private environment: EnvironmentConfig) {}

  addThirdPartyRecruitmentAudience(
    projectId: string,
    methodId: string,
    thirdPartyRecruitmentAudience: IThirdPartyProviderAudience
  ): Observable<ResearchAudience> {
    return this.http.post<ResearchAudience>(
      `${this.environment.endPoint}project/${projectId}/research/${methodId}/audience/third-party-recruitment`,
      thirdPartyRecruitmentAudience
    );
  }

  addDirectUploadAudience(projectId: string, methodId: string, audience: IDirectUploadAudience): Observable<ResearchAudience> {
    return this.http.post<ResearchAudience>(`${this.environment.endPoint}project/${projectId}/research/${methodId}/audience/direct-upload`, audience);
  }

  addDirectRecruitmentAudience(projectId: string, methodId: string, audience: IDirectRecruitmentAudience): Observable<ResearchAudience> {
    return this.http.post<ResearchAudience>(`${this.environment.endPoint}project/${projectId}/research/${methodId}/audience/direct-recruitment`, audience);
  }

  addDeployToPanelAudience(projectId: string, methodId: string, deployToPanelAudience: IDeployToPanelAudience): Observable<ResearchAudience> {
    return this.http.post<ResearchAudience>(
      `${this.environment.endPoint}project/${projectId}/research/${methodId}/audience/deploy-to-panel`,
      deployToPanelAudience
    );
  }

  updateThirdPartyRecruitmentAudience(
    projectId: string,
    methodId: string,
    audienceId: string,
    thirdPartyRecruitmentAudience: IUpdateThirdPartyProviderAudience
  ): Observable<ResearchAudience> {
    return this.http.patch<ResearchAudience>(
      `${this.environment.endPoint}project/${projectId}/research/${methodId}/audience/${audienceId}/third-party-recruitment`,
      thirdPartyRecruitmentAudience
    );
  }

  updateDirectUploadParticipantInvites(projectId: string, methodId: string, audienceId: string, audience: IDirectUploadAudience): Observable<ResearchAudience> {
    return this.http.patch<ResearchAudience>(
      `${this.environment.endPoint}project/${projectId}/research/${methodId}/audience/${audienceId}/direct-upload/participants`,
      audience
    );
  }

  updateDirectRecruitmentAudience(projectId: string, methodId: string, audienceId: string, audience: IDirectRecruitmentAudience): Observable<ResearchAudience> {
    return this.http.patch<ResearchAudience>(
      `${this.environment.endPoint}project/${projectId}/research/${methodId}/audience/${audienceId}/direct-recruitment`,
      audience
    );
  }

  updateDeployToPanelAudience(projectId: string, methodId: string, audienceId: string, audience: IDeployToPanelAudience): Observable<ResearchAudience> {
    return this.http.patch<ResearchAudience>(
      `${this.environment.endPoint}project/${projectId}/research/${methodId}/audience/${audienceId}/deploy-to-panel`,
      audience
    );
  }

  deleteAudience(projectId: string, methodId: string, audienceId: string): Observable<boolean> {
    return this.http.delete<boolean>(`${this.environment.endPoint}project/${projectId}/research/${methodId}/audience/${audienceId}`);
  }

  getAudienceDefaultInvitationSettings(
    audienceType: ResearchAudienceTypeEnum,
    researchType: ResearchType,
    purpose: 'INVITATION' | 'REMINDER'
  ): Observable<AudienceDefaultInvitationSettings | null> {
    return this.http.get<{ templates: AudienceDefaultInvitationSettings[] }>(`${this.environment.endPoint}audience/communications/email-templates`).pipe(
      map(
        (defaultTemplates) =>
          defaultTemplates.templates.find((dt) => dt.audienceType === audienceType && dt.researchType === researchType && dt.purpose === purpose) ?? null
      ),
      catchError(() => of(null))
    );
  }

  patchAudienceStatus(audiences: ResearchAudience[], audienceId: string, status: AudienceStatusEnum): void {
    const audience = audiences.find((a) => a.id === audienceId);

    if (!audience) {
      this.notificationService.open({ level: NotificationLevel.Error, title: 'Error', message: 'Audience not found. Please refresh the page.' });
      return;
    }

    audience.status = status;
  }

  validateParticipantEmails(emails: string[]): Observable<IParticipantEmailValidationResponse | null> {
    return this.http
      .post<IParticipantEmailValidationResponse>(`${this.environment.endPoint}audience/validate-participant-emails`, { emails })
      .pipe(catchError(() => of(null)));
  }
  getAudienceStats(projectId: string, methodId: string): Observable<AudienceStats[]> {
    return this.http
      .get<AudienceStats[]>(`${this.environment.endPoint}project/${projectId}/research/${methodId}/audience/stats`)
      .pipe(catchError(() => of(null)));
  }
  refreshFeasibility(projectId: string, methodId: string): Observable<ResearchAudience[] | null> {
    return this.http
      .post<ResearchAudience[]>(
        `${this.environment.endPoint}project/${projectId}/research/${methodId}/audience/third-party-recruitment/refresh-feasibility`,
        {}
      )
      .pipe(catchError(() => of(null)));
  }

  getPureSpectrumIntegrationAttributes(localizations: string[]): Observable<PureSpectrumIntegrationAttribute[]> {
    return this.http
      .post<PureSpectrumIntegrationAttribute[]>(`${this.environment.endPoint}audience/pure-spectrum/attributes`, { localizations })
      .pipe(catchError(() => of([])));
  }

  getPureSpectrumFeasibility(request: IGetFeasibilityAndPriceRequest): Observable<IFeasibilityAndPrice | null> {
    return this.http.post<IFeasibilityAndPrice>(`${this.environment.endPoint}audience/pure-spectrum/feasibility`, request).pipe(catchError(() => of(null)));
  }

  getExternalProjectIds(): Observable<ExternalProjectItem[]> {
    return this.http.get<ExternalProjectItem[]>(`${this.environment.endPoint}audience/pure-spectrum/external-projects`).pipe(catchError(() => of([])));
  }

  updatePureSpectrumStatus(projectId: string, methodId: string, audienceId: string, action: AudienceStatusEnum): Observable<boolean> {
    return this.http.put(`${this.environment.endPoint}project/${projectId}/research/${methodId}/audience/${audienceId}/pure-spectrum/status`, { action }).pipe(
      map(() => true),
      catchError(() => of(false))
    );
  }

  fullLaunchPureSpectrum(projectId: string, methodId: string, audienceId: string): Observable<boolean> {
    return this.http.post(`${this.environment.endPoint}project/${projectId}/research/${methodId}/audience/${audienceId}/pure-spectrum/full-launch`, {}).pipe(
      map(() => true),
      catchError(() => of(false))
    );
  }

  private stringifyQueryValue(query: Query): void {
    query.queryRows.forEach((queryRow) => {
      if (queryRow.queryValue) {
        queryRow.queryValue = JSON.stringify(queryRow.queryValue);
      }
      if (queryRow.hasSubgroup) {
        this.stringifyQueryValue(queryRow.subgroup);
      }
    });
  }
}
